/**
 * Market catalog and money types.
 *
 * Two rules govern everything in this file:
 *
 * 1. No floats in money. Odds are integer milli-odds (×1.85 is 1850) and stakes
 *    and payouts are whole chips. Floating point drift in a ledger is the kind
 *    of bug that is invisible for a month and then unfixable.
 * 2. Odds are stored on the bet. A market can reprice a dozen times after a bet
 *    is placed; the bet pays what it was offered, sportsbook-style.
 */
export const MIN_MILLI_ODDS = 1_010; // ×1.01
export const MAX_MILLI_ODDS = 50_000; // ×50.00
export function formatOdds(milliOdds) {
    return `×${(milliOdds / 1000).toFixed(2)}`;
}
/**
 * American (moneyline) odds.
 *
 * The internal representation never changes — everything is still milli-odds,
 * and every payout is computed from those. This is purely how a price is shown.
 *
 *   decimal >= 2.00  ->  +(d - 1) x 100     what 100 staked wins
 *   decimal <  2.00  ->  -100 / (d - 1)     what must be staked to win 100
 *
 * Even money is +100 by convention, never -100.
 */
export function toAmerican(milliOdds) {
    const decimal = milliOdds / 1000;
    if (decimal >= 2)
        return Math.round((decimal - 1) * 100);
    return -Math.round(100 / (decimal - 1));
}
export function formatAmerican(milliOdds) {
    const american = toAmerican(milliOdds);
    return american > 0 ? `+${american}` : String(american);
}
export function formatOddsAs(milliOdds, format) {
    return format === 'decimal' ? (milliOdds / 1000).toFixed(2) : formatAmerican(milliOdds);
}
/** Total returned to the bettor on a win, stake included. Always rounds down. */
export function payoutFor(stake, milliOdds) {
    return Math.floor((stake * milliOdds) / 1000);
}
/** Profit only — what the bet slip shows as "to win". */
export function profitFor(stake, milliOdds) {
    return payoutFor(stake, milliOdds) - stake;
}
export const MARKET_DEFS = {
    win: {
        key: 'win',
        label: 'Match result',
        phase: 'pregame',
        needsLobby: false,
        selections: ['yes', 'no'],
        selectionLabels: ['Wins', 'Loses'],
    },
    kills_ou: {
        key: 'kills_ou',
        label: 'Her kills',
        phase: 'pregame',
        needsLobby: false,
        lineGranularity: 1,
        selections: ['over', 'under'],
        selectionLabels: ['Over', 'Under'],
    },
    deaths_ou: {
        key: 'deaths_ou',
        label: 'Her deaths',
        phase: 'pregame',
        needsLobby: false,
        lineGranularity: 1,
        selections: ['over', 'under'],
        selectionLabels: ['Over', 'Under'],
    },
    assists_ou: {
        key: 'assists_ou',
        label: 'Her assists',
        phase: 'pregame',
        needsLobby: false,
        lineGranularity: 1,
        selections: ['over', 'under'],
        selectionLabels: ['Over', 'Under'],
    },
    kda_ou: {
        key: 'kda_ou',
        label: 'Her KDA',
        phase: 'pregame',
        needsLobby: false,
        lineGranularity: 0.5,
        selections: ['over', 'under'],
        selectionLabels: ['Over', 'Under'],
    },
    total_kills_ou: {
        key: 'total_kills_ou',
        label: 'Total game kills (both teams)',
        phase: 'pregame',
        needsLobby: false,
        lineGranularity: 1,
        selections: ['over', 'under'],
        selectionLabels: ['Over', 'Under'],
    },
    deathless_10: {
        key: 'deathless_10',
        label: 'Deathless to 10:00',
        phase: 'pregame',
        needsLobby: false,
        selections: ['yes', 'no'],
        selectionLabels: ['Yes', 'No'],
    },
    perfect_game: {
        key: 'perfect_game',
        label: 'Perfect game (zero deaths)',
        phase: 'pregame',
        needsLobby: false,
        selections: ['yes', 'no'],
        selectionLabels: ['Yes', 'No'],
    },
    level_race_11: {
        key: 'level_race_11',
        label: 'First to level 11 (vs her lane)',
        phase: 'live',
        needsLobby: true,
        selections: ['her', 'opponent'],
        selectionLabels: ['Hien', 'Lane opponent'],
    },
    game_time_ou: {
        key: 'game_time_ou',
        label: 'Game length (minutes)',
        phase: 'pregame',
        needsLobby: false,
        lineGranularity: 1,
        selections: ['over', 'under'],
        selectionLabels: ['Over', 'Under'],
    },
    first_blood: {
        key: 'first_blood',
        label: 'First blood',
        phase: 'live',
        needsLobby: true,
        selections: ['her_team', 'enemy'],
        selectionLabels: ['Her team', 'Enemy'],
    },
    cs_at_10_ou: {
        key: 'cs_at_10_ou',
        label: 'Her CS at 10:00',
        phase: 'live',
        needsLobby: true,
        lineGranularity: 10,
        selections: ['over', 'under'],
        selectionLabels: ['Over', 'Under'],
    },
    team_kills_ou: {
        key: 'team_kills_ou',
        label: 'Her team total kills',
        phase: 'live',
        needsLobby: true,
        lineGranularity: 1,
        selections: ['over', 'under'],
        selectionLabels: ['Over', 'Under'],
    },
    first_dragon: {
        key: 'first_dragon',
        label: 'First dragon',
        phase: 'live',
        needsLobby: true,
        selections: ['her_team', 'enemy'],
        selectionLabels: ['Her team', 'Enemy'],
    },
    first_tower: {
        key: 'first_tower',
        label: 'First tower',
        phase: 'live',
        needsLobby: true,
        selections: ['her_team', 'enemy'],
        selectionLabels: ['Her team', 'Enemy'],
    },
    solo_kill_lane: {
        key: 'solo_kill_lane',
        label: 'Solo-kills her lane opponent',
        phase: 'live',
        needsLobby: true,
        selections: ['yes', 'no'],
        selectionLabels: ['Yes', 'No'],
    },
};
//# sourceMappingURL=defs.js.map